// Tree + bush variants (owner 2026-11-02: 'generate several various versions of the trees, make varying heights and sizes,
// tall short, skinny, wider, add some bushes too'). Every shape keeps the owner-picked painted-canopy look (design E in
// trees.ts): painted leaf-cluster cards on round lobes whose card normals are the lobe's outward direction, so the colour
// bands (violet shade → greens → warm sunlit top) read as soft round masses for any yaw. Render-only; the sim's hit radii
// (core/terrain.ts TREE_VARIANTS / BUSH_VARIANTS) match each trunk / leafy mass built here.
//  - each variant is 2 parts: bark trunk (toon, ink outline) + ONE foliage mesh (leaf cards + solid lobe cores merged; a
//    `core` vertex flag keeps the cores opaque and a band darker), so a variant costs 2 draws + 1 outline
//  - only the cores cast shadows (maskShadowNode), like before: alpha cards would cast square shadows
import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { attribute, bool, color, float, max, min, mix, mx_noise_float, positionGeometry, smoothstep, texture, uv, vec3 } from 'three/tsl';
import { PAINT_TINT } from './paintTint';
import { BUSH_VARIANTS, TREE_VARIANTS } from '../core/terrain';
import { RenderStyle, toonMaterial } from './toon';
import { blob, paintedLeafTexture, trunkGeo, type TreePart } from './trees';

const GP = positionGeometry;
/** [radius, x, y, z, sx, sy, sz] — an ellipsoid canopy lobe (local units) */
type Lobe = [number, number, number, number, number?, number?, number?];
type Limb = [number, number, number, number, number];

export interface FloraShape {
  parts: TreePart[];
  /** trunk profile (local units) for stumps / cut faces: height, base radius, top radius (0 = no trunk) */
  trunkH: number; r0: number; r1: number;
  /** canopy centre height + radius (falling-tree collider, leaf bursts, landing test), and the top of the crown */
  canopyY: number; canopyR: number; top: number;
  /** flame anchors when it burns: [x, y, z, size] */
  crown: [number, number, number, number][];
  /** base leaf tint (multiplies the instance colour) */
  tint: [number, number, number];
}

let sd = 7;
const rnd = () => { sd = (sd * 16807) % 2147483647; return sd / 2147483647; };

/** painted leaf-card + core foliage over the given lobes. `card` = card size, `dens` = cards per unit lobe area. */
function foliage(lobes: Lobe[], card: number, dens: number, coreK = 0.78, detail = 3): THREE.BufferGeometry {
  const plane = new THREE.PlaneGeometry(card, card); plane.deleteAttribute('normal');
  const parts: THREE.BufferGeometry[] = []; const n = new THREE.Vector3(), q = new THREE.Quaternion(), m = new THREE.Matrix4(), Z = new THREE.Vector3(0, 0, 1), nn3 = new THREE.Vector3();
  for (const [r, x, y, z, sx = 1, sy = 1, sz = 1] of lobes) {
    const count = Math.round(dens * r * r * Math.cbrt(sx * sy * sz) ** 2);
    for (let i = 0; i < count; i++) {
      n.set(rnd() * 2 - 1, rnd() * 2 - 1, rnd() * 2 - 1); if (n.lengthSq() > 1) { i--; continue; } n.normalize();
      const depth = 0.72 + rnd() * 0.36;
      nn3.set(n.x / sx, n.y / sy, n.z / sz).normalize(); // ellipsoid surface normal
      q.setFromUnitVectors(Z, nn3).multiply(new THREE.Quaternion().setFromAxisAngle(Z, rnd() * 6.283));
      const sc = 0.75 + rnd() * 0.5;
      m.compose(new THREE.Vector3(x + n.x * r * depth * sx, y + n.y * r * depth * 0.9 * sy, z + n.z * r * depth * sz), q, new THREE.Vector3(sc, sc, sc));
      const c = plane.clone().applyMatrix4(m);
      const cnt = c.attributes.position.count, nn = new Float32Array(cnt * 3);
      for (let k = 0; k < cnt; k++) { nn[k * 3] = nn3.x; nn[k * 3 + 1] = nn3.y * 0.85 + (depth - 0.8) * 0.4; nn[k * 3 + 2] = nn3.z; }
      c.setAttribute('normal', new THREE.BufferAttribute(nn, 3));
      c.setAttribute('core', new THREE.BufferAttribute(new Float32Array(cnt), 1));
      parts.push(c);
    }
  }
  lobes.forEach(([r, x, y, z, sx = 1, sy = 1, sz = 1], i) => {
    const b = blob(r * coreK, x, y, z, i * 3.1, sx, 0.92 * sy, sz, 0.07, detail);
    const cnt = b.attributes.position.count;
    b.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(cnt * 2), 2));
    b.setAttribute('core', new THREE.BufferAttribute(new Float32Array(cnt).fill(1), 1));
    parts.push(b);
  });
  return mergeGeometries(parts, false)!;
}

let flowerTex: THREE.Texture | null = null;
/** small rounded leaves (R coverage, G per-leaf value) with five-petal blossoms painted into B */
function flowerLeafTexture(): THREE.Texture {
  if (flowerTex) return flowerTex;
  const S = 256, c = document.createElement('canvas'); c.width = c.height = S; const g = c.getContext('2d')!;
  let s0 = 5; const r = () => { s0 = (s0 * 16807) % 2147483647; return s0 / 2147483647; };
  g.globalCompositeOperation = 'lighter';
  for (let i = 0; i < 52; i++) {
    const a = r() * 6.283, d = Math.sqrt(r()) * S * 0.34, x = S / 2 + Math.cos(a) * d, y = S / 2 + Math.sin(a) * d;
    const len = S * (0.07 + r() * 0.05), wid = len * (0.5 + r() * 0.15), rot = a + (r() - 0.5) * 1.6;
    g.save(); g.translate(x, y); g.rotate(rot); g.globalCompositeOperation = 'source-over'; g.fillStyle = `rgb(255,${Math.round(110 + r() * 145)},0)`;
    g.beginPath(); g.moveTo(-len * 0.5, 0); g.quadraticCurveTo(0, -wid, len * 0.55, 0); g.quadraticCurveTo(0, wid, -len * 0.5, 0); g.fill(); g.restore();
  }
  for (let i = 0; i < 9; i++) { // blossoms sit on top of the leaves (B = petal, B+G high = centre)
    const a = r() * 6.283, d = Math.sqrt(r()) * S * 0.28, x = S / 2 + Math.cos(a) * d, y = S / 2 + Math.sin(a) * d, pr = S * (0.022 + r() * 0.012);
    g.fillStyle = 'rgb(255,200,255)';
    for (let k = 0; k < 5; k++) { const b = k * 1.2566 + a; g.beginPath(); g.arc(x + Math.cos(b) * pr, y + Math.sin(b) * pr, pr * 0.85, 0, 6.283); g.fill(); }
    g.fillStyle = 'rgb(255,255,120)'; g.beginPath(); g.arc(x, y, pr * 0.55, 0, 6.283); g.fill();
  }
  flowerTex = new THREE.CanvasTexture(c); flowerTex.colorSpace = THREE.NoColorSpace; flowerTex.anisotropy = 4; return flowerTex;
}

type Pal = [number, number, number, number, number];
/** shade (violet) → deep → mid → sunlit → warm highlight */
const TREE_PAL: Pal = [0x23284a, 0x17452a, 0x2b6129, 0x4f8238, 0x86a854];
const BUSH_PAL: Pal = [0x22304a, 0x1a5530, 0x337a34, 0x5c9c40, 0x9cc65c];

const mats = new Map<string, THREE.Material>();
/** one foliage material per palette (shared by every variant using it) */
function foliageMat(pal: Pal, flowers: boolean): THREE.Material {
  const key = `${pal[0]}|${flowers}`; let m = mats.get(key) as THREE.MeshBasicNodeMaterial | undefined;
  if (m) return m;
  m = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide, alphaTest: 0.5 });
  const tx = texture(flowers ? flowerLeafTexture() : paintedLeafTexture(), uv());
  const core = attribute('core', 'float'), ny = attribute('normal', 'vec3').y;
  const noise = mx_noise_float(GP.mul(2.2)).mul(0.12);
  const kLeaf = ny.mul(0.5).add(0.5).add(noise).add(tx.g.sub(0.7).mul(0.25));
  const kCore = min(ny.mul(0.5).add(0.5).mul(0.8).add(noise).sub(0.04), 0.6); // the solid core sits a band darker
  const k = mix(kLeaf, kCore, core);
  let col = mix(color(pal[0]), color(pal[1]), smoothstep(0.22, 0.3, k));
  col = mix(col, color(pal[2]), smoothstep(0.46, 0.52, k));
  col = mix(col, color(pal[3]), smoothstep(0.64, 0.7, k));
  col = mix(col, color(pal[4]), smoothstep(0.82, 0.88, k));
  if (flowers) { // blossoms: pale pink petals with a warm centre, shaded a little on the dark side
    const petal = smoothstep(0.5, 0.6, tx.b).mul(float(1).sub(core)), ctr = smoothstep(0.75, 0.85, tx.b.mul(tx.g));
    const fc = mix(mix(color(0xd76a92), color(0xffd0e0), smoothstep(0.35, 0.6, kLeaf)), color(0xffe07a), ctr);
    col = mix(col, fc, petal);
  }
  m.colorNode = col.mul(PAINT_TINT); // MAP SYSTEM: unlit painted foliage follows the time of day (white by day)
  m.opacityNode = max(tx.r, core);
  return m;
}
/** shadow pass: only the solid lobe cores cast (alpha cards would cast squares) */
export const coreShadowMask = () => attribute('core', 'float').greaterThan(0.5);

let barkM: THREE.Material | null = null;
function bark(rs: RenderStyle): THREE.Material {
  if (barkM) return barkM;
  barkM = rs === 'cel' ? toonMaterial({ color: 0xc69568 }) : new THREE.MeshStandardNodeMaterial({ color: 0xc69568, roughness: 0.9 });
  (barkM as THREE.NodeMaterial).colorNode = mix(color(0x5e3e28), color(0x9a7450), smoothstep(0.3, 0.7, mx_noise_float(vec3(GP.x.mul(32), GP.y.mul(2.8), GP.z.mul(32))).mul(0.5).add(0.5)));
  return barkM;
}

const ring = (n: number, rad: number, r: number, y: number, s: [number, number, number] = [1, 1, 1], off = 0, jit = 0.12): Lobe[] =>
  Array.from({ length: n }, (_, k) => { const a = off + (k / n) * Math.PI * 2; return [r * (1 - jit + (k % 3) * jit), Math.cos(a) * rad, y + ((k * 7) % 3 - 1) * 0.08, Math.sin(a) * rad, ...s] as Lobe; });

interface TreeSpec { trunk: [number, number, number, Limb[]]; lobes: Lobe[]; card: number; dens: number; canopyY: number; canopyR: number; tint: [number, number, number]; seed: number }
const TREE_SPECS: TreeSpec[] = [
  // classic painted canopy (owner-picked design E: same lobes, card density and seed as trees.ts paintedTree)
  { trunk: [1.5, 0.17, 0.12, [[1.15, 0.08, -0.5, 0.1, 1.35], [1.1, 0.075, 0.48, -0.15, 1.4], [1.0, 0.07, 0.1, 0.55, 1.42], [1.0, 0.065, -0.05, -0.55, 1.45]]],
    lobes: [[1.3, 0, 2.95, 0], [1.05, 0.95, 2.6, 0.3], [1.0, -0.92, 2.65, -0.25], [0.98, 0.15, 2.55, -0.95], [0.95, -0.1, 2.6, 0.95], [0.9, 0.2, 3.75, 0.1]],
    card: 0.95, dens: 70, canopyY: 2.9, canopyR: 1.15, tint: [1, 1, 1], seed: 7 },
  // poplar: tall + skinny column of stacked lobes on a long trunk
  { trunk: [2.2, 0.14, 0.08, [[0.9, 0.05, -0.25, 0.1, 1.8], [0.85, 0.05, 0.28, -0.1, 2.0], [0.8, 0.045, 0.05, 0.3, 2.3]]],
    lobes: [[0.8, 0, 2.7, 0, 0.85, 1.15, 0.85], [0.84, 0.08, 3.45, -0.05, 0.85, 1.1, 0.85], [0.74, -0.06, 4.2, 0.06, 0.85, 1.1, 0.85], [0.56, 0.04, 4.85, 0, 0.85, 1.15, 0.85],
      [0.52, 0.32, 3.05, 0.28], [0.5, -0.32, 3.7, -0.22], [0.46, 0.22, 4.35, -0.26], [0.5, -0.25, 2.55, 0.3]],
    card: 0.82, dens: 74, canopyY: 3.7, canopyR: 0.95, tint: [0.92, 1.0, 0.92], seed: 11 },
  // round: short trunk, wide low dome
  { trunk: [0.95, 0.24, 0.17, [[0.95, 0.1, -0.95, 0.1, 0.72], [0.9, 0.1, 0.9, -0.2, 0.78], [0.85, 0.09, 0.15, 0.95, 0.8], [0.85, 0.09, -0.1, -0.95, 0.8]]],
    lobes: [[1.35, 0, 2.05, 0, 1.08, 0.82, 1.08], ...ring(6, 1.3, 0.95, 1.7, [1, 0.8, 1], 0.3), [0.85, 0.1, 2.75, 0.05], ...ring(3, 0.7, 0.7, 2.5, [1, 0.85, 1], 1.1)],
    card: 0.95, dens: 66, canopyY: 2.0, canopyR: 1.6, tint: [1.04, 1.02, 0.94], seed: 13 },
  // broad: tall trunk with spreading limbs, wide flat-topped crown
  { trunk: [2.5, 0.22, 0.14, [[1.6, 0.1, -0.85, 0.1, 2.0], [1.55, 0.1, 0.8, -0.3, 2.1], [1.5, 0.09, 0.2, 0.85, 2.15], [1.5, 0.09, -0.2, -0.85, 2.2], [1.0, 0.08, 0.05, 0.1, 2.4]]],
    lobes: [[1.25, 0, 4.05, 0, 1.15, 0.7, 1.15], ...ring(6, 1.8, 1.0, 3.65, [1, 0.72, 1], 0.1), ...ring(3, 0.95, 0.85, 4.45, [1, 0.75, 1], 0.6)],
    card: 0.95, dens: 62, canopyY: 3.8, canopyR: 1.9, tint: [0.97, 1.0, 1.0], seed: 17 },
  // sapling: thin young trunk, small sparse crown
  { trunk: [1.7, 0.09, 0.05, [[0.62, 0.035, -0.6, 0, 1.2], [0.55, 0.03, 0.6, 0.3, 1.4], [0.5, 0.03, 0.1, -0.6, 1.5]]],
    lobes: [[0.62, 0, 2.15, 0], [0.48, 0.45, 1.85, 0.1], [0.45, -0.38, 1.95, -0.2], [0.4, 0.05, 2.62, 0.05], [0.38, 0.05, 1.9, 0.42]],
    card: 0.7, dens: 70, canopyY: 2.1, canopyR: 0.7, tint: [1.08, 1.06, 0.9], seed: 19 },
  // tiered: a spindle trunk through stacked flat leaf pads, cooler blue-green
  { trunk: [4.1, 0.17, 0.035, []],
    lobes: [[1.55, 0, 1.55, 0, 1, 0.36, 1], [1.3, 0.05, 2.3, 0.02, 1, 0.38, 1], [1.02, -0.03, 3.0, 0, 1, 0.4, 1], [0.74, 0.02, 3.62, 0, 1, 0.45, 1], [0.42, 0, 4.12, 0, 1, 0.8, 1]],
    card: 0.85, dens: 60, canopyY: 2.6, canopyR: 1.4, tint: [0.84, 0.97, 1.03], seed: 23 },
];
interface BushSpec { lobes: Lobe[]; card: number; dens: number; flowers: boolean; tint: [number, number, number]; seed: number }
const BUSH_SPECS: BushSpec[] = [
  // dome: a round leafy mound (r 0.85 × BUSH_K)
  { lobes: [[0.6, 0, 0.5, 0, 1.15, 0.92, 1.15], [0.42, 0.42, 0.34, 0.22], [0.4, -0.4, 0.36, -0.18], [0.38, 0.05, 0.32, -0.45], [0.36, -0.1, 0.34, 0.45]], card: 0.5, dens: 230, flowers: false, tint: [1, 1, 1], seed: 29 },
  // spread: wide and low, flowering (r 1.1 × BUSH_K)
  { lobes: [[0.62, 0, 0.42, 0, 1.45, 0.66, 1.3], ...ring(5, 0.68, 0.42, 0.32, [1.1, 0.8, 1.1], 0.4)], card: 0.48, dens: 230, flowers: true, tint: [1, 1, 1], seed: 31 },
  // upright: a taller narrow clump (r 0.62 × BUSH_K)
  { lobes: [[0.5, 0, 0.55, 0, 1, 1.15, 1], [0.42, 0.1, 1.05, 0.05], [0.32, -0.05, 1.45, 0], [0.3, 0.26, 0.8, -0.14], [0.3, -0.24, 0.72, 0.16]], card: 0.46, dens: 230, flowers: false, tint: [0.94, 1.0, 0.96], seed: 37 },
];

const lobeTop = (l: Lobe[]) => Math.max(...l.map(([r, , y, , , sy = 1]) => y + r * sy));
const crownOf = (l: Lobe[], k = 1.05): [number, number, number, number][] => l.slice(0, 7).map(([r, x, y, z, sx = 1]) => [x, y, z, Math.max(0.35, r * k * Math.sqrt(sx))]);

/** Build every tree variant (index = TREE_VARIANTS). */
export function buildTreeVariants(rs: RenderStyle): FloraShape[] {
  if (TREE_SPECS.length !== TREE_VARIANTS.length) throw new Error('tree variant table mismatch');
  return TREE_SPECS.map((t) => {
    sd = t.seed;
    const [h, r0, r1, limbs] = t.trunk;
    const fol = foliage(t.lobes, t.card, t.dens);
    const crown = crownOf(t.lobes); crown.push([0, 0.4, 0.12, Math.min(0.6, r0 * 3.5)]); // + a flame licking the trunk base
    return { parts: [{ geo: trunkGeo(h, r0, r1, limbs), mat: bark(rs), outline: 0.025 }, { geo: fol, mat: foliageMat(TREE_PAL, false), outline: 0 }],
      trunkH: h, r0, r1, canopyY: t.canopyY, canopyR: t.canopyR, top: lobeTop(t.lobes), crown, tint: t.tint };
  });
}
/** bushes are modelled in metres and drawn × BUSH_K × the prop's s (sim radii in BUSH_VARIANTS include it) */
export const BUSH_K = 1.35;
/** Build every bush variant (index = BUSH_VARIANTS). */
export function buildBushVariants(): FloraShape[] {
  if (BUSH_SPECS.length !== BUSH_VARIANTS.length) throw new Error('bush variant table mismatch');
  return BUSH_SPECS.map((b) => {
    sd = b.seed;
    const fol = foliage(b.lobes, b.card, b.dens, 0.8, 2);
    const top = lobeTop(b.lobes), cy = top * 0.45;
    return { parts: [{ geo: fol, mat: foliageMat(BUSH_PAL, b.flowers), outline: 0 }], trunkH: 0, r0: 0.05, r1: 0.03, canopyY: cy, canopyR: Math.max(...b.lobes.map(([r, x, , z, sx = 1]) => Math.hypot(x, z) + r * sx)) * 0.8, top, crown: crownOf(b.lobes, 0.9), tint: b.tint };
  });
}
/** local trunk radius at height y (matches trunkGeo's root flare) */
export const trunkRadius = (f: FloraShape, y: number): number => (f.r0 + (f.r1 - f.r0) * Math.min(1, y / Math.max(1e-3, f.trunkH))) * (y < 0.35 ? 1 + (0.35 - y) * 0.9 : 1);
void bool;
